import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Upload, KeyRound, RefreshCw, Euro, ChevronDown, ChevronUp } from "lucide-react";
import { api, API } from "@/lib/api";

const inputCls = "w-full bg-[#0D0D0C] border border-[#F4F0EB]/10 rounded-lg px-4 py-3 font-sub text-sm text-[#F4F0EB] placeholder:text-[#6E695E] focus:border-[#C88D51]/60 focus:outline-none transition-colors";
const genCode = () => Math.random().toString(36).slice(2, 8).toUpperCase();

export const GalleriesManager = () => {
  const [galleries, setGalleries] = useState([]);
  const [form, setForm] = useState({ client_name: "", access_code: genCode(), balance_due: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/galleries").then(({ data }) => setGalleries(data));
  useEffect(() => { load(); }, []);

  const loadPhotos = (gid) => api.get(`/galleries/${gid}/photos`).then(({ data }) => setPhotos(data));

  const toggle = (gid) => {
    if (expanded === gid) { setExpanded(null); return; }
    setExpanded(gid);
    setPhotos([]);
    loadPhotos(gid);
  };

  const create = async () => {
    if (!form.client_name || !form.access_code) { toast.error("Nom du client et code d'accès requis."); return; }
    setLoading(true);
    try {
      await api.post("/galleries", { ...form, balance_due: parseInt(form.balance_due, 10) || 0 });
      toast.success("Galerie privée créée ✦");
      setForm({ client_name: "", access_code: genCode(), balance_due: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setLoading(false);
  };

  const remove = async (gid) => { await api.delete(`/galleries/${gid}`); toast.success("Galerie supprimée"); if (expanded === gid) setExpanded(null); load(); };

  const upload = async (gid, file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/galleries/${gid}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo ajoutée ✦");
      loadPhotos(gid);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'upload"); }
    setUploading(false);
  };

  const removePhoto = async (gid, pid) => { await api.delete(`/galleries/${gid}/photos/${pid}`); loadPhotos(gid); load(); };

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <div className="bg-[#161513] border border-[#F4F0EB]/8 rounded-xl p-6 h-fit">
        <h3 className="font-serif-d text-xl mb-4">Nouvelle galerie privée</h3>
        <div className="space-y-3">
          <input data-testid="admin-gallery-client-name" placeholder="Nom du client / projet" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className={inputCls} />
          <div className="flex gap-2">
            <input data-testid="admin-gallery-code" placeholder="Code d'accès" value={form.access_code} onChange={(e) => setForm({ ...form, access_code: e.target.value.toUpperCase() })} className={inputCls} />
            <button data-testid="admin-gallery-regen-code" onClick={() => setForm({ ...form, access_code: genCode() })} className="btn-ghost px-3 shrink-0" title="Générer un code"><RefreshCw className="w-4 h-4" /></button>
          </div>
          <div className="relative">
            <Euro className="w-4 h-4 text-[#C88D51] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input data-testid="admin-gallery-balance" type="number" min="0" placeholder="Solde à régler (€) — 0 si déjà payé" value={form.balance_due} onChange={(e) => setForm({ ...form, balance_due: e.target.value })} className={inputCls + " pl-11"} />
          </div>
          <p className="font-sub text-xs text-[#6E695E]">Si un solde est indiqué, le client devra le régler en ligne avant de pouvoir télécharger ses photos.</p>
        </div>
        <button data-testid="admin-gallery-create" onClick={create} disabled={loading} className="btn-accent w-full mt-4 py-3 font-sub text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer la galerie
        </button>
      </div>

      <div className="lg:col-span-2 space-y-3">
        {galleries.length === 0 && <p className="font-sub text-[#6E695E]">Aucune galerie privée pour le moment.</p>}
        {galleries.map((g) => (
          <div key={g.id} data-testid={`admin-gallery-row-${g.id}`} className="bg-[#161513] border border-[#F4F0EB]/8 rounded-xl p-5">
            <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
              <div>
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <span className="font-serif-d text-lg">{g.client_name}</span>
                  <span className="font-mono-a text-xs text-[#C88D51] flex items-center gap-1"><KeyRound className="w-3 h-3" /> {g.access_code}</span>
                  <span className={`font-mono-a text-[0.6rem] uppercase px-2 py-0.5 rounded-full ${g.balance_status === "paid" ? "bg-green-500/15 text-green-400" : "bg-[#C88D51]/20 text-[#E4A869]"}`}>
                    {g.balance_status === "paid" ? "solde réglé" : `solde ${g.balance_due}€ dû`}
                  </span>
                </div>
                <div className="font-mono-a text-xs text-[#A39E93]">{g.photo_count} photo{g.photo_count > 1 ? "s" : ""}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button data-testid={`admin-gallery-toggle-${g.id}`} onClick={() => toggle(g.id)} className="btn-ghost px-3 py-2 text-xs flex items-center gap-1">
                  {expanded === g.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} Photos
                </button>
                <button data-testid={`admin-gallery-delete-${g.id}`} onClick={() => remove(g.id)} className="btn-ghost px-3 py-2 text-xs flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Supprimer</button>
              </div>
            </div>

            {expanded === g.id && (
              <div className="mt-5 pt-5 border-t border-[#F4F0EB]/8">
                <label className="inline-flex items-center gap-2 btn-ghost px-4 py-2.5 font-sub text-xs cursor-pointer mb-4">
                  <input data-testid={`admin-gallery-upload-${g.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(g.id, e.target.files[0]); e.target.value = ""; }} />
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Ajouter une photo
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                  {photos.map((p) => (
                    <div key={p.id} className="group relative aspect-square overflow-hidden rounded-lg border border-[#F4F0EB]/8">
                      <img src={`${API}/galleries/${g.id}/photo/${p.id}?code=${encodeURIComponent(g.access_code)}`} alt={p.filename} className="w-full h-full object-cover" loading="lazy" />
                      <button data-testid={`admin-gallery-photo-delete-${p.id}`} onClick={() => removePhoto(g.id, p.id)}
                        className="absolute inset-0 bg-[#0D0D0C]/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Trash2 className="w-4 h-4 text-[#F4F0EB]" />
                      </button>
                    </div>
                  ))}
                </div>
                {photos.length === 0 && <p className="font-sub text-xs text-[#6E695E]">Aucune photo dans cette galerie.</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
